/**
 * Directives — extensible `$`-prefixed dynamic values that resolve at
 * render time without an AI round-trip.
 *
 * Mirrors `vercel-labs/json-render`'s directive system. A directive is
 * an object whose first key matches a registered name (e.g. `$format`,
 * `$math`, `$truncate`); the registered resolver is invoked with the
 * raw object and the resolution context, and its return value replaces
 * the original.
 *
 * Why directives in addition to the existing `$computed`:
 * - Tighter syntax — `{$format: "currency", value: {$state:"/x"}}` vs
 *   `{$computed: "formatCurrency", args: {value: {$state:"/x"}}}`.
 *   Saves tokens on AI generation.
 * - The directive name is itself the operation, easier for the AI to
 *   pick out of the system prompt.
 * - Extensible: host apps register custom directives without modifying
 *   the resolver. Standard set in `standard-directives.ts`.
 *
 * Built-in expression keys (`$state`, `$bindState`, `$cond`, `$template`,
 * `$computed`, `$filter`) are reserved — defineDirective rejects any
 * name that collides.
 */

import type { ResolveContext } from "./expressions"

/**
 * A directive resolver. Receives the raw directive value (already
 * recognised as containing the directive's `$`-key) and the live
 * resolution context. May call back into `resolvePropValue` to
 * recursively resolve sub-values.
 */
export type DirectiveResolver = (value: Record<string, unknown>, ctx: ResolveContext) => unknown

export interface DirectiveDefinition {
  /** The `$`-prefixed key that triggers this directive. */
  name: string
  /** One-line description for the system prompt. */
  description: string
  /** Resolver invoked at render time. */
  resolve: DirectiveResolver
}

/** Reserved keys handled by built-in expression resolution. */
const BUILT_IN_KEYS = new Set([
  "$state",
  "$bindState",
  "$cond",
  "$then",
  "$else",
  "$template",
  "$computed",
  "$filter",
])

/**
 * Validate-and-pass-through helper. Throws if the name doesn't start
 * with `$` or collides with a built-in key.
 */
export function defineDirective(definition: DirectiveDefinition): DirectiveDefinition {
  if (!definition.name.startsWith("$")) {
    throw new Error(`Directive name must start with "$": got "${definition.name}"`)
  }
  if (BUILT_IN_KEYS.has(definition.name)) {
    throw new Error(`Directive name "${definition.name}" conflicts with a built-in expression key`)
  }
  return definition
}

export type DirectiveRegistry = Map<string, DirectiveDefinition>

/**
 * Build a registry from a flat list. Used at render-time setup.
 */
export function createDirectiveRegistry(directives: DirectiveDefinition[]): DirectiveRegistry {
  const registry: DirectiveRegistry = new Map()
  for (const d of directives) {
    if (registry.has(d.name)) {
      throw new Error(`Duplicate directive name: ${d.name}`)
    }
    registry.set(d.name, d)
  }
  return registry
}

/**
 * Find the directive matching a plain-object value. Returns undefined
 * when no directive applies. Throws when more than one directive key
 * is present (ambiguous spec).
 *
 * Called *after* all built-in expressions (`$state`, `$cond`, etc.)
 * have been checked in the main resolver, so built-ins always take
 * precedence.
 */
export function findDirective(
  value: Record<string, unknown>,
  registry: DirectiveRegistry | undefined
): DirectiveDefinition | undefined {
  if (!registry || registry.size === 0) return undefined
  let match: DirectiveDefinition | undefined
  for (const [key, def] of registry) {
    if (key in value) {
      if (match) {
        throw new Error(
          `Ambiguous directive: object has multiple directive keys ("${match.name}" and "${key}")`
        )
      }
      match = def
    }
  }
  return match
}
